/**
 * @fileoverview Hook de Data Layer para gestión de opciones de filtros del inbox de Mercately
 * @module useInboxFilterOptions
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchInboxAgentsIfEmpty } from '../../../../actions/inbox/retailerUserActions';
import { fetchContactGroupsIfEmpty } from '../../../../actions/inbox/contactGroupsActions';
import { getTagsIfEmpty } from '../../../../actions/inbox/tagActions';
import { getFunnelsIfEmpty } from '../../../../actions/inbox/funnelsActions';
import { fetchCampaignsIfEmpty } from '../../../../actions/inbox/campaignsActions';

/**
 * @typedef {Object} FilterOption
 * @description Estructura base para las opciones de filtro del sistema
 * @property {string|number} id - Identificador único de la opción
 * @property {string|number} value - Valor utilizado para filtrado
 * @property {string} label - Texto mostrado al usuario
 */

/**
 * @typedef {Object} AgentOption
 * @description Opción de filtro para agentes del retailer
 * @property {string} id - ID único del agente
 * @property {string} value - Valor del agente para filtrado
 * @property {string} label - Nombre completo o email del agente
 * @property {boolean} active - Estado activo del agente
 */

/**
 * @typedef {Object} CampaignOption
 * @description Opción de filtro para campañas
 * @property {string} id - ID único de la campaña
 * @property {string} value - Valor de la campaña para filtrado
 * @property {string} label - Nombre de la campaña
 */

/**
 * @typedef {Object} FunnelStepOption
 * @description Paso de embudo para filtrado granular
 * @property {string} id - ID único del paso del embudo
 * @property {string} value - Valor del paso para filtrado
 * @property {string} label - Nombre del paso del embudo
 */

/**
 * @typedef {Object} FunnelOption
 * @description Opción de filtro para embudos de ventas
 * @property {string} id - ID único del embudo
 * @property {string} value - Valor del embudo para filtrado
 * @property {string} label - Nombre del embudo
 * @property {FunnelStepOption[]} funnel_steps - Pasos del embudo disponibles
 */

/**
 * @typedef {Object} TagOption
 * @description Opción de filtro para etiquetas
 * @property {string} id - ID único de la etiqueta
 * @property {string} value - Valor de la etiqueta para filtrado
 * @property {string} label - Texto de la etiqueta
 * @property {string} [color] - Color asociado a la etiqueta
 */

/**
 * @typedef {Object} CustomerListOption
 * @description Opción de filtro para listas de contactos
 * @property {string} id - ID único de la lista de contactos
 * @property {string} value - Valor de la lista para filtrado
 * @property {string} label - Nombre de la lista de contactos
 */

/**
 * @typedef {Object} InboxFilterOptionsReturn
 * @description Objeto retornado por el hook con todas las opciones de filtro
 * @property {AgentOption[]} agentListOptions - Opciones de agentes disponibles
 * @property {CampaignOption[]} campaignOptions - Opciones de campañas disponibles
 * @property {CustomerListOption[]} customerListOptions - Opciones de listas de contactos
 * @property {FunnelOption[]} funnelOptions - Opciones de embudos con sus pasos
 * @property {TagOption[]} tagOptions - Opciones de etiquetas disponibles
 * @property {boolean} loading - Estado de carga consolidado de todas las opciones
 */

/**
 * Hook de Data Layer para gestión de opciones de filtros del inbox de Mercately.
 *
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades,
 * enfocándose en la carga y normalización de datos de filtrado del inbox.
 *
 * @function useInboxFilterOptions
 * @category Hook - Data Layer
 *
 * @description
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **Data Layer**: Solo carga de opciones de filtro y normalización de datos
 * - ✅ **Redux Integration**: Utiliza selectores optimizados con datos memoizados
 * - ✅ **Performance**: useCallback, useMemo, y optimizaciones de re-renderizado
 * - ✅ **Error Handling**: Manejo robusto con validaciones de arrays vacíos
 * - ✅ **Debug Tracking**: Rastreo de re-renders en modo desarrollo
 *
 * **Responsabilidades:**
 * - Carga inicial de opciones de filtro (agentes, campañas, embudos, etiquetas, listas)
 * - Normalización de datos de diferentes fuentes Redux en formato uniforme
 * - Optimización de re-renders mediante memoización profunda
 * - Consolidación de estados de carga de múltiples recursos
 * - Transformación de datos para compatibilidad con componentes de filtro
 *
 * **NO Maneja:**
 * - ❌ Lógica de filtrado o aplicación de filtros
 * - ❌ Estado de UI de componentes de filtro
 * - ❌ Operaciones de guardado o mutación de datos
 * - ❌ Validación de formularios de filtro
 *
 * **Flujo de Datos:**
 * 1. **Inicialización**: Dispara acciones Redux para cargar datos faltantes
 * 2. **Selección**: Obtiene datos de diferentes reducers Redux
 * 3. **Normalización**: Transforma datos en formato uniforme para filtros
 * 4. **Memoización**: Optimiza re-renders con memoización profunda
 * 5. **Consolidación**: Combina estados de carga y retorna opciones procesadas
 *
 * **Estructura de Datos Procesados:**
 * ```javascript
 * {
 *   agentListOptions: [{ id, value, label, active }],
 *   campaignOptions: [{ id, value, label }],
 *   customerListOptions: [{ id, value, label }],
 *   funnelOptions: [{ id, value, label, funnel_steps: [...] }],
 *   tagOptions: [{ id, value, label, color }],
 *   loading: boolean
 * }
 * ```
 *
 * @returns {InboxFilterOptionsReturn} Opciones de filtro normalizadas y estados de carga
 *
 * @see {@link useInboxFiltersForm} Para manejo de formulario de filtros
 * @see {@link useInboxFiltersUI} Para estado de UI de filtros
 * @see {@link InboxFiltersComponent} Para componente que utiliza estas opciones
 *
 * @since 1.0.0
 * @version 3.1.0 - Optimizaciones de performance y debug tracking
 *
 * @example
 * ```javascript
 * // Uso básico en componente de filtros
 * const InboxFilters = () => {
 *   const {
 *     agentListOptions,
 *     campaignOptions,
 *     funnelOptions,
 *     tagOptions,
 *     loading
 *   } = useInboxFilterOptions();
 *
 *   if (loading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   return (
 *     <div className="tw-wrapper">
 *       <SelectMC options={agentListOptions} placeholder="Seleccionar agente" />
 *       <SelectMC options={campaignOptions} placeholder="Seleccionar campaña" />
 *       <SelectMC options={tagOptions} placeholder="Seleccionar etiquetas" />
 *     </div>
 *   );
 * };
 * ```
 *
 * @example
 * ```javascript
 * // Uso avanzado con filtros anidados
 * const AdvancedFilters = () => {
 *   const { funnelOptions, loading } = useInboxFilterOptions();
 *   const [selectedFunnel, setSelectedFunnel] = useState(null);
 *
 *   const selectedFunnelSteps = useMemo(() => {
 *     return selectedFunnel?.funnel_steps || [];
 *   }, [selectedFunnel]);
 *
 *   return (
 *     <div className="tw-wrapper">
 *       <SelectMC
 *         options={funnelOptions}
 *         onChange={setSelectedFunnel}
 *         placeholder="Seleccionar embudo"
 *       />
 *       {selectedFunnelSteps.length > 0 && (
 *         <SelectMC
 *           options={selectedFunnelSteps}
 *           placeholder="Seleccionar paso del embudo"
 *         />
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */
const useInboxFilterOptions = () => {
  const dispatch = useDispatch();

  /**
   * Función memoizada para cargar todas las opciones de filtro necesarias.
   *
   * Optimiza las llamadas a Redux evitando re-creaciones innecesarias y
   * carga datos solo si no están presentes en el store.
   *
   * @async
   * @function fetchOptions
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Flujo de Carga:**
   * 1. **Tags**: Carga etiquetas disponibles para filtrado
   * 2. **Funnels**: Carga embudos de venta con sus pasos
   * 3. **Campaigns**: Carga campañas activas del retailer
   * 4. **Contact Groups**: Carga listas de contactos organizadas
   * 5. **Agents**: Carga agentes del retailer filtrados por rol
   *
   * **Optimizaciones:**
   * - Utiliza acciones "*IfEmpty" que verifican existencia antes de cargar
   * - Evita llamadas redundantes al API mediante validación en Redux
   * - Se ejecuta solo una vez por montaje del componente
   *
   * @example
   * ```javascript
   * // Llamada interna automática
   * useEffect(() => {
   *   fetchOptions(); // Se ejecuta al montar el componente
   * }, [fetchOptions]);
   * ```
   */
  const fetchOptions = useCallback(() => {
    dispatch(getTagsIfEmpty());
    dispatch(getFunnelsIfEmpty());
    dispatch(fetchCampaignsIfEmpty());
    dispatch(fetchContactGroupsIfEmpty());
    dispatch(fetchInboxAgentsIfEmpty());
  }, [dispatch]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  // Selectors - Obtienen datos normalizados de diferentes reducers Redux
  const agentList = useSelector((state) => state.inboxAgentsReducer.inboxAgentList || []);
  const agentsLoading = useSelector((state) => state.inboxAgentsReducer.loading || false);

  const customerList = useSelector((state) => state.contactGroupsReducer.contactGroupsList || []);
  const customerListLoading = useSelector((state) => state.contactGroupsReducer.loading || false);

  const campaigns = useSelector((state) => state.mainReducer.campaigns || []);
  const campaignsLoading = useSelector((state) => state.mainReducer.loadingCampaigns || false);

  const funnels = useSelector((state) => state.funnelsReducer?.funnels || []);
  const funnelsLoading = useSelector((state) => state.funnelsReducer?.loadingFunnels || false);

  const tags = useSelector((state) => state.tagsReducer?.tags || []);
  const tagsLoading = useSelector((state) => state.tagsReducer?.loading || false);

  /**
   * Transforma la lista de agentes en opciones de filtro normalizadas.
   *
   * Procesa datos de agentes desde Redux y los convierte en un formato
   * uniforme compatible con componentes de selección.
   *
   * @function agentListOptions
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Transformaciones Aplicadas:**
   * - Combina first_name y last_name en label legible
   * - Fallback a email si no hay nombre disponible
   * - Preserva estado activo para filtrado adicional
   * - Validación de array para evitar errores
   *
   * **Estructura de Salida:**
   * ```javascript
   * {
   *   id: "agent_123",
   *   value: "agent_123",
   *   label: "Juan Pérez" | "juan.perez@empresa.com",
   *   active: true
   * }
   * ```
   *
   * @returns {AgentOption[]} Lista de agentes normalizados para filtros
   */
  const agentListOptions = useMemo(() => {
    if (!Array.isArray(agentList)) return [];
    return agentList.map((agent) => {
      const fullName = agent.first_name && agent.last_name ? `${agent.first_name} ${agent.last_name}` : agent.email;
      return {
        id: agent.id,
        value: agent.id,
        label: fullName,
        active: agent.active
      };
    });
  }, [agentList]);

  /**
   * Transforma listas de contactos en opciones de filtro normalizadas.
   *
   * Procesa datos de grupos de contactos desde Redux para uso en filtros
   * de segmentación de clientes.
   *
   * @function customerListOptions
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Propósito:**
   * - Permite filtrar conversaciones por listas específicas de contactos
   * - Facilita segmentación de clientes en el inbox
   * - Estructura uniforme compatible con componentes de selección
   *
   * **Estructura de Salida:**
   * ```javascript
   * {
   *   id: "list_456",
   *   value: "list_456",
   *   label: "Clientes VIP"
   * }
   * ```
   *
   * @returns {CustomerListOption[]} Listas de contactos normalizadas
   */
  const customerListOptions = useMemo(() => {
    if (!Array.isArray(customerList)) return [];
    return customerList.map((list) => ({
      id: list.id,
      value: list.id,
      label: list.name
    }));
  }, [customerList]);

  /**
   * Transforma campañas en opciones de filtro normalizadas.
   *
   * Procesa datos de campañas desde Redux manteniendo compatibilidad
   * con el formato específico utilizado en el reducer principal.
   *
   * @function campaignOptions
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Características:**
   * - Utiliza estructura value/label existente en Redux
   * - Mantiene compatibilidad con componentes existentes
   * - Agrega ID normalizado para consistencia
   *
   * **Estructura de Entrada (Redux):**
   * ```javascript
   * { value: "campaign_789", label: "Campaña Black Friday" }
   * ```
   *
   * **Estructura de Salida:**
   * ```javascript
   * {
   *   id: "campaign_789",
   *   value: "campaign_789",
   *   label: "Campaña Black Friday"
   * }
   * ```
   *
   * @returns {CampaignOption[]} Campañas normalizadas para filtros
   */
  const campaignOptions = useMemo(() => {
    if (!Array.isArray(campaigns)) return [];
    return campaigns.map((campaign) => ({
      id: campaign.id,
      value: campaign.id,
      label: campaign.name
    }));
  }, [campaigns]);

  /**
   * Transforma embudos con sus pasos en opciones de filtro jerárquicas.
   *
   * Procesa datos complejos de embudos incluyendo sus pasos anidados
   * para permitir filtrado granular por estado del embudo.
   *
   * @function funnelOptions
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Funcionalidad Avanzada:**
   * - Estructura jerárquica con embudos y sus pasos
   * - Permite filtrado en dos niveles (embudo → paso específico)
   * - Validación robusta de pasos anidados
   * - Fallback a array vacío si no hay pasos
   *
   * **Estructura Jerárquica:**
   * ```javascript
   * {
   *   id: "funnel_321",
   *   value: "funnel_321",
   *   label: "Embudo de Ventas",
   *   funnel_steps: [
   *     {
   *       id: "step_001",
   *       value: "step_001",
   *       label: "Prospecto Inicial"
   *     },
   *     {
   *       id: "step_002",
   *       value: "step_002",
   *       label: "Calificación"
   *     }
   *   ]
   * }
   * ```
   *
   * **Casos de Uso:**
   * - Filtrar por embudo completo
   * - Filtrar por paso específico dentro del embudo
   * - Análisis de conversión por etapa
   *
   * @returns {FunnelOption[]} Embudos con pasos anidados normalizados
   */
  const funnelOptions = useMemo(() => {
    if (!Array.isArray(funnels)) return [];
    return funnels.map((funnel) => ({
      id: funnel.id,
      value: funnel.id,
      label: funnel.name,
      funnel_steps: funnel.funnel_steps?.map((step) => ({
        id: step.id,
        value: step.id,
        label: step.name
      })) || []
    }));
  }, [funnels]);

  /**
   * Transforma etiquetas en opciones de filtro con soporte de color.
   *
   * Procesa etiquetas desde Redux incluyendo información de color
   * para representación visual en componentes de filtro.
   *
   * @function tagOptions
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Características:**
   * - Soporte para múltiples formatos de etiquetas (label/name)
   * - Preservación de información de color (color/tagColor)
   * - Fallbacks robustos para diferentes estructuras de datos
   * - Compatible con componentes visuales que requieren color
   *
   * **Estructura de Salida:**
   * ```javascript
   * {
   *   id: "tag_654",
   *   value: "tag_654",
   *   label: "Cliente VIP",
   *   color: "#ff6b35" | "#007bff"
   * }
   * ```
   *
   * **Fallbacks Implementados:**
   * - `tag.label || tag.name` para compatibilidad de texto
   * - `tag.color || tag.tagColor` para compatibilidad de color
   * - Validación de array para prevenir errores
   *
   * @returns {TagOption[]} Etiquetas normalizadas con información de color
   */
  const tagOptions = useMemo(() => {
    if (!Array.isArray(tags)) return [];
    return tags.map((tag) => ({
      id: tag.id,
      value: tag.id,
      label: tag.label || tag.name,
      color: tag.color || tag.tagColor
    }));
  }, [tags]);

  /**
   * Estado de carga consolidado de todos los recursos.
   *
   * Combina los estados individuales de carga de cada recurso
   * para proporcionar un indicador único de estado del hook.
   *
   * @function loading
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Lógica de Consolidación:**
   * - TRUE si cualquier recurso está cargando
   * - FALSE solo cuando todos los recursos han terminado de cargar
   * - Optimizado con useMemo para evitar cálculos innecesarios
   *
   * **Recursos Monitoreados:**
   * - agentsLoading (estado de carga de agentes)
   * - customerListLoading (estado de carga de listas de contactos)
   * - campaignsLoading (estado de carga de campañas)
   * - funnelsLoading (estado de carga de embudos)
   * - tagsLoading (estado de carga de etiquetas)
   *
   * @returns {boolean} Estado de carga consolidado
   */
  const loading = useMemo(() => (
    agentsLoading || customerListLoading || campaignsLoading || funnelsLoading || tagsLoading
  ), [agentsLoading, customerListLoading, campaignsLoading, funnelsLoading, tagsLoading]);

  /**
   * Objeto de retorno memoizado con todas las opciones de filtro procesadas.
   *
   * Optimiza re-renders mediante memoización del objeto de retorno completo,
   * asegurando que los componentes consumidores solo se re-rendericen cuando
   * los datos realmente cambien.
   *
   * @function returnObject
   * @memberof useInboxFilterOptions
   * @private
   *
   * @description
   * **Optimizaciones de Performance:**
   * - useMemo evita creación de nuevo objeto en cada render
   * - Array de dependencias específico con todas las opciones procesadas
   * - Estructura inmutable que facilita comparaciones en React
   *
   * **Contenido del Objeto:**
   * - agentListOptions: Agentes transformados para filtrado
   * - campaignOptions: Campañas normalizadas
   * - customerListOptions: Listas de contactos procesadas
   * - funnelOptions: Embudos con pasos anidados
   * - tagOptions: Etiquetas con información de color
   * - loading: Estado consolidado de carga
   *
   * **Estructura Consistente:**
   * Todas las opciones siguen el formato { id, value, label, ...extras }
   * para garantizar compatibilidad con componentes de selección.
   *
   * @returns {InboxFilterOptionsReturn} Objeto inmutable con opciones de filtro
   */
  return useMemo(() => ({
    agentListOptions,
    campaignOptions,
    customerListOptions,
    funnelOptions,
    tagOptions,
    loading
  }), [
    agentListOptions,
    campaignOptions,
    customerListOptions,
    funnelOptions,
    loading,
    tagOptions
  ]);
};

export default useInboxFilterOptions;
